import React from 'react';
import { 
    TableRow, TableCell, Typography, Chip, IconButton, Box, Tooltip 
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import GppBadIcon from '@mui/icons-material/GppBad';

const RoleListItem = ({ role, onEdit, onDelete }) => {
    const permissions = role.permissions || [];
    const displayPermissions = permissions.slice(0, 2);
    const remainingCount = permissions.length - displayPermissions.length;
    
    // Format name if it's an object (robustness check from previous fix)
    const roleName = typeof role.name === 'object' ? (role.name.name || 'Unknown Role') : (role.name || 'Unknown Role');
    
    // Format date
    const createdAt = role.createdAt ? new Date(role.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    }) : 'N/A';

    return (
        <TableRow 
            hover
            sx={{ 
                '&:hover': { bgcolor: '#f8fafc !important' },
                borderBottom: '1px solid #f1f5f9',
            }}
        >
            <TableCell sx={{ pl: 3, py: 2, borderBottom: '1px solid #f1f5f9' }}>
                <Typography sx={{ color: '#4338ca', fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.3 }}>
                    {roleName}
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: '0.75rem', mt: 0.35 }}>
                    Created: {createdAt}
                </Typography>
            </TableCell>
            
            <TableCell sx={{ px: 2, py: 2, borderBottom: '1px solid #f1f5f9' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    {displayPermissions.map((perm, index) => {
                        const permLabel = typeof perm === 'object' ? (perm.name || perm.key || perm.code || 'Perm') : String(perm);
                        return (
                            <Chip 
                                key={index} 
                                label={permLabel} 
                                size="small" 
                                sx={{ 
                                    bgcolor: '#f1f5f9',
                                    color: '#475569',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '4px',
                                    height: 22,
                                    fontSize: '0.6875rem',
                                    fontWeight: 600,
                                    letterSpacing: '0.01em',
                                    '& .MuiChip-label': {
                                        px: 0.85,
                                    },
                                }}
                            />
                        );
                    })}
                    {remainingCount > 0 && (
                        <Tooltip title={permissions.slice(2).map(p => typeof p === 'object' ? (p.name || p.key || p.code || 'N/A') : String(p)).join(', ')}>
                            <Chip 
                                label={`+${remainingCount} more`} 
                                size="small" 
                                sx={{ 
                                    bgcolor: '#eef2ff',
                                    color: '#4338ca',
                                    border: '1px solid #c7d2fe',
                                    borderRadius: '4px',
                                    height: 22,
                                    fontSize: '0.6875rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    '& .MuiChip-label': {
                                        px: 0.85,
                                    },
                                }}
                            />
                        </Tooltip>
                    )}
                    {permissions.length === 0 && (
                        <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>No permissions assigned</Typography>
                    )}
                </Box>
            </TableCell>
            
            {/* Temporarily hidden: MFA Status */}
            {false && (
                <TableCell>
                    {role.enforceMfa ? (
                        <Chip 
                            icon={<SecurityIcon style={{ fontSize: '1rem' }} />} 
                            label="MFA Enforced" 
                            size="small" 
                            color="success" 
                            variant="filled"
                        />
                    ) : (
                        <Chip 
                            icon={<GppBadIcon style={{ fontSize: '1rem' }} />} 
                            label="MFA Optional" 
                            size="small" 
                            color="warning" 
                            variant="outlined"
                        />
                    )}
                </TableCell>
            )}
            
            <TableCell align="right" sx={{ pr: 3, py: 2, borderBottom: '1px solid #f1f5f9' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5 }}>
                    <IconButton 
                        size="small" 
                        onClick={() => onEdit(role)}
                        disabled={!role.isEditable}
                        sx={{ 
                            p: 0.5,
                            color: 'primary.main',
                            bgcolor: 'transparent',
                            borderRadius: '4px',
                            '&:hover': { 
                                bgcolor: 'rgba(var(--primary-color-rgb, 65, 70, 224), 0.08)' 
                            },
                            '&.Mui-disabled': {
                                opacity: 0.35,
                            },
                        }}
                        aria-label="Edit role"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                    </IconButton>
                    <IconButton 
                        size="small" 
                        onClick={() => onDelete(role.id)}
                        disabled={!role.isEditable}
                        sx={{ 
                            p: 0.5,
                            color: '#ef4444',
                            bgcolor: 'transparent',
                            borderRadius: '4px',
                            '&:hover': { 
                                bgcolor: 'rgba(239, 68, 68, 0.08)' 
                            },
                            '&.Mui-disabled': {
                                opacity: 0.35,
                            },
                        }}
                        aria-label="Delete role"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                    </IconButton>
                </Box>
            </TableCell>
        </TableRow>
    );
};

export default RoleListItem;
